import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSymbol } from '../../services/SymbolsService';
import {
    parseQuantityValue,
    serializeQuantityValue,
    splitSymbolPair,
} from './quantityTemplateUtils';
import './QuantityTemplate.css';

/**
 * props:
 * - id
 * - text
 * - quantity
 * - multiplier
 * - symbol
 * - onChange
 */
function QuantityTemplate(props) {
    const [symbolInfo, setSymbolInfo] = useState(null);
    const [unit, setUnit] = useState('base');
    const [amount, setAmount] = useState('');

    const { base, quote } = useMemo(() => {
        if (symbolInfo?.base && symbolInfo?.quote) {
            return { base: symbolInfo.base, quote: symbolInfo.quote };
        }
        return splitSymbolPair(props.symbol);
    }, [props.symbol, symbolInfo]);

    useEffect(() => {
        if (!props.symbol) {
            setSymbolInfo(null);
            return;
        }

        getSymbol(props.symbol)
            .then((symbol) => setSymbolInfo(symbol))
            .catch(() => setSymbolInfo(null));
    }, [props.symbol]);

    const emitUpdate = useCallback((nextUnit, nextAmount) => {
        const serialized = serializeQuantityValue(nextUnit, nextAmount);

        props.onChange({ target: { id: props.id, value: serialized.quantity } });
        props.onChange({ target: { id: `${props.id}Multiplier`, value: serialized.quantityMultiplier } });
    }, [props.id, props.onChange]);

    useEffect(() => {
        const parsed = parseQuantityValue(props.quantity, props.multiplier);
        setUnit(parsed.unit);
        setAmount(parsed.amount);
    }, [props.multiplier, props.quantity]);

    function onUnitChange(nextUnit) {
        if (nextUnit === unit) return;
        setUnit(nextUnit);
        emitUpdate(nextUnit, amount);
    }

    function onAmountChange(event) {
        const nextAmount = event.target.value;
        setAmount(nextAmount);
        emitUpdate(unit, nextAmount);
    }

    return (
        <div className="form-group qty-template">
            <label htmlFor={`${props.id}-amount`} className="qty-template__label">{props.text}</label>

            <div className="qty-template__unit-toggle" role="group" aria-label="Order size unit">
                <button
                    type="button"
                    className={`qty-template__unit-btn${unit === 'base' ? ' is-active' : ' is-inactive'}`}
                    onClick={() => onUnitChange('base')}
                    aria-pressed={unit === 'base'}
                >
                    {base}
                    <small>Coin</small>
                </button>
                <button
                    type="button"
                    className={`qty-template__unit-btn${unit === 'quote' ? ' is-active' : ' is-inactive'}`}
                    onClick={() => onUnitChange('quote')}
                    aria-pressed={unit === 'quote'}
                >
                    {quote}
                    <small>Currency</small>
                </button>
            </div>

            <input
                id={`${props.id}-amount`}
                type="number"
                className="form-control"
                min="0"
                step="any"
                placeholder="0"
                value={amount}
                onChange={onAmountChange}
            />
        </div>
    );
}

export default QuantityTemplate;